import React, {useMemo} from 'react';
import {Box, Grid, Stack, Typography} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import type {NoProps} from 'types';
import type {TextVariant} from 'theme/types';
import {Card, FlexBox, Scene, Spacer, Text} from 'components';

type Props = NoProps;

/**
 * Typography variants showcased on the page, in display order. Each maps to a
 * MUI typography variant that {@link Text} can render via its `variant` prop.
 */
const typographyVariants: {variant: TextVariant; description: string}[] = [
  {variant: 'h1', description: 'Page title'},
  {variant: 'h2', description: 'Section / card title'},
  {variant: 'h3', description: 'Sub-section (uppercase weight 700)'},
  {variant: 'h4', description: 'Minor heading'},
  {variant: 'subtitle1', description: 'Subtitle'},
  {variant: 'body1', description: 'Default body text'},
  {variant: 'body2', description: 'Dense body / input text'},
  {variant: 'caption', description: 'Captions and labels'},
  {variant: 'button', description: 'Button label'},
];

const sampleText = 'The quick brown fox jumps over the lazy dog';

/**
 * Pick a readable text color (near-black or near-white) for a given background
 * hex, using the standard relative-luminance threshold.
 */
const getContrastText = (
  hex: string,
  darkText: string,
  lightText: string,
): string => {
  const normalized = hex.replace('#', '').slice(0, 6);
  if (normalized.length < 6) {
    return darkText;
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? darkText : lightText;
};

type Swatch = {name: string; value: string};

/** Flatten a record of colors (string or nested record) into named swatches. */
const toSwatches = (colors: Record<string, unknown>, prefix = ''): Swatch[] =>
  Object.entries(colors).flatMap(([key, value]) => {
    const name = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') {
      return [{name, value}];
    }
    if (value && typeof value === 'object') {
      return toSwatches(value as Record<string, unknown>, name);
    }
    return [];
  });

const ColorSwatch: React.FC<Swatch> = ({name, value}) => {
  const {palette, colors} = useTheme();
  const contrast = getContrastText(
    value,
    palette.text.primary,
    colors.offWhite,
  );
  return (
    <Box
      sx={{
        backgroundColor: value,
        color: contrast,
        borderRadius: 1,
        border: `1px solid ${colors.warmGreen06}`,
        p: 1.5,
        height: 84,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}>
      <Typography variant="body2" sx={{color: contrast, fontWeight: 700}}>
        {name}
      </Typography>
      <Typography
        variant="caption"
        sx={{color: contrast, textTransform: 'uppercase'}}>
        {value}
      </Typography>
    </Box>
  );
};

const SwatchGroup: React.FC<{title: string; swatches: Swatch[]}> = ({
  title,
  swatches,
}) => (
  <>
    <Spacer size={1} />
    <Text variant="h3" kind="primary">
      {title}
    </Text>
    <Spacer size={1} />
    <Grid container spacing={1.5}>
      {swatches.map(swatch => (
        <Grid key={swatch.name} size={{xs: 6, sm: 4, md: 3}}>
          <ColorSwatch {...swatch} />
        </Grid>
      ))}
    </Grid>
    <Spacer size={2} />
  </>
);

type BackgroundLevels = {level1: string; level2: string; level3: string};

/**
 * A single labelled background panel. Nests its children (the next, deeper
 * level) inside, so a `beige`/`brown`/`burgundy` family renders as panels
 * within panels going level1 → level2 → level3.
 */
const BackgroundPanel: React.FC<{
  level: string;
  value: string;
  children?: React.ReactNode;
}> = ({level, value, children}) => {
  const {palette, colors} = useTheme();
  const contrast = getContrastText(
    value,
    palette.text.primary,
    colors.offWhite,
  );
  return (
    <Box
      sx={{
        backgroundColor: value,
        borderRadius: 2,
        p: 2,
        border: `1px solid ${colors.warmGreen06}`,
      }}>
      <FlexBox horizontal sx={{justifyContent: 'space-between'}}>
        <Typography variant="body2" sx={{color: contrast, fontWeight: 700}}>
          {level}
        </Typography>
        <Typography
          variant="caption"
          sx={{color: contrast, textTransform: 'uppercase'}}>
          {value}
        </Typography>
      </FlexBox>
      {children && <Spacer size={1.5} />}
      {children}
    </Box>
  );
};

/**
 * Renders one background family as nested panels, demonstrating how the three
 * levels stack to create depth when panels are placed within panels.
 */
const BackgroundFamily: React.FC<{title: string; levels: BackgroundLevels}> = ({
  title,
  levels,
}) => (
  <Box>
    <Text variant="h3" kind="primary">
      {title}
    </Text>
    <Spacer size={1} />
    <BackgroundPanel level="level1" value={levels.level1}>
      <BackgroundPanel level="level2" value={levels.level2}>
        <BackgroundPanel level="level3" value={levels.level3} />
      </BackgroundPanel>
    </BackgroundPanel>
  </Box>
);

export const ThemeShowcaseScene: React.FC<Props> = () => {
  const theme = useTheme();
  const {palette, colors, status} = theme;

  const baseSwatches = useMemo(
    () =>
      toSwatches({
        burgundy: colors.burgundy,
        burgundy75: colors.burgundy75,
        burgundy50: colors.burgundy50,
        warmGreen: colors.warmGreen,
        terracotta: colors.terracotta,
        offWhite: colors.offWhite,
        beige: colors.beige,
        brown: colors.brown,
        lightBeige: colors.lightBeige,
        textPrimary: palette.text.primary,
        textSecondary: palette.text.secondary,
      }),
    [colors, palette.text.primary, palette.text.secondary],
  );

  const semanticSwatches = useMemo(
    () =>
      toSwatches({
        info: colors.info,
        success: palette.success.main,
        approve: colors.approve,
        reject: colors.reject,
        warning: palette.warning.main,
        error: palette.error.main,
      }),
    [colors, palette.success.main, palette.warning.main, palette.error.main],
  );

  const statusSwatches = useMemo(() => toSwatches(status), [status]);

  return (
    <Scene browserTitle={'Theme'}>
      <Scene.Header title={'Fargepalett'} />
      <Scene.Content>
        <Grid container spacing={2}>
          {/* Typography */}
          <Grid size={{xs: 12, md: 7}}>
            <Card level="level3">
              <Card.Header>
                <Text variant="h2" kind="primary">
                  {'Typography'}
                </Text>
              </Card.Header>
              <Card.Content>
                <Stack spacing={2.5} divider={<Divider />}>
                  {typographyVariants.map(({variant, description}) => {
                    const style = theme.typography[variant];
                    return (
                      <Box key={variant}>
                        <FlexBox
                          horizontal
                          sx={{justifyContent: 'space-between'}}>
                          <Text variant="caption" kind="secondary">
                            {variant}
                          </Text>
                          <Text variant="caption" kind="secondary">
                            {`${String(style.fontSize ?? '')} · ${String(
                              style.fontWeight ?? '',
                            )} · lh ${String(style.lineHeight ?? '')}`}
                          </Text>
                        </FlexBox>
                        <Spacer size={0.5} />
                        <Text variant={variant} kind="primary">
                          {sampleText}
                        </Text>
                        <Text variant="caption" kind="secondary">
                          {description}
                        </Text>
                      </Box>
                    );
                  })}
                </Stack>
              </Card.Content>
            </Card>
          </Grid>

          {/* Palette */}
          <Grid size={{xs: 12, md: 5}}>
            <Card level="level3">
              <Card.Header>
                <Text variant="h2" kind="primary">
                  {'Palette'}
                </Text>
              </Card.Header>
              <Card.Content>
                <SwatchGroup title="Brand & neutrals" swatches={baseSwatches} />
                <SwatchGroup title="Semantic" swatches={semanticSwatches} />
                <SwatchGroup title="Status" swatches={statusSwatches} />
              </Card.Content>
            </Card>
          </Grid>

          {/* Backgrounds */}
          <Grid size={{xs: 12}}>
            <Card level="level3">
              <Card.Header>
                <Text variant="h2" kind="primary">
                  {'Backgrounds'}
                </Text>
              </Card.Header>
              <Card.Content>
                <Text variant="body2" kind="secondary">
                  {
                    'Each family defines three background levels. Nest panels within panels to build depth.'
                  }
                </Text>
                <Spacer size={2} />
                <Grid container spacing={2}>
                  {(['beige', 'brown', 'burgundy'] as const).map(name => (
                    <Grid key={name} size={{xs: 12, md: 4}}>
                      <BackgroundFamily
                        title={name}
                        levels={palette.background[name]}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Card.Content>
            </Card>
          </Grid>
        </Grid>
      </Scene.Content>
    </Scene>
  );
};

const Divider: React.FC = () => {
  const {colors} = useTheme();
  return <Box sx={{borderTop: `1px solid ${colors.beige}`}} />;
};
